import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getSetting, resolveMediaUrl } from "../lib/api";

const FALLBACK_SLIDES = [
  "https://customer-assets.emergentagent.com/job_her-box-studio/artifacts/o4wprocg_WhatsApp%20Image%202026-04-28%20at%206.16.41%20AM%20%281%29.jpeg",
  "https://customer-assets.emergentagent.com/job_her-box-studio/artifacts/4iykmmgz_WhatsApp%20Image%202026-04-28%20at%206.16.41%20AM.jpeg",
  "https://customer-assets.emergentagent.com/job_her-box-studio/artifacts/8eqcy8du_WhatsApp%20Image%202026-04-29%20at%207.40.43%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_her-box-studio/artifacts/li16wmou_WhatsApp%20Image%202026-04-29%20at%207.24.21%20PM.jpeg",
  "https://customer-assets.emergentagent.com/job_her-box-studio/artifacts/bfkhvfaj_WhatsApp%20Image%202026-04-27%20at%2010.06.02%20PM.jpeg",
];

const INTERVAL = 3000;

export default function HeroCarousel() {
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    getSetting("hero_images")
      .then((s) => {
        if (s && Array.isArray(s.value) && s.value.length > 0) {
          setSlides(s.value);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), INTERVAL);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 50) setActive((i) => (i - 1 + slides.length) % slides.length);
    else if (dx < -50) setActive((i) => (i + 1) % slides.length);
    touchStartX.current = null;
  };

  return (
    <section
      data-testid="hero-carousel"
      className="relative w-full h-[100vh] overflow-hidden bg-[#0A0A0A]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((src, i) => (
        <AnimatePresence key={i}>
          {active === i && (
            <motion.div
              key={`slide-${i}`}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1.2, ease: "easeOut" }, scale: { duration: 4, ease: "linear" } }}
              className="absolute inset-0"
            >
              <img src={resolveMediaUrl(src)} alt="" className="w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
            </motion.div>
          )}
        </AnimatePresence>
      ))}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.35) 35%, rgba(10,10,10,0.4) 65%, rgba(10,10,10,0.85) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(10,10,10,0.7) 0%, rgba(10,10,10,0.0) 45%, rgba(10,10,10,0.0) 100%)",
        }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 h-full flex items-center pt-24 pb-16">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-ui text-[12px] tracking-[0.4em] uppercase mb-6"
            style={{ color: "#F5D565" }}
          >
            ✦ Her Era · 2026
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25 }}
            className="font-serif-display text-[14vw] md:text-[7.5rem] lg:text-[9rem] leading-[0.95] tracking-tighter text-white"
          >
            Unbox
            <br />
            Your <em className="gold-text not-italic font-light">Style.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="font-body mt-7 max-w-lg leading-relaxed"
            style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}
          >
            Signature Pieces. Uniquely crafted, boldly defined. Worn by the women who walk in like a sentence and leave like a chapter.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10"
          >
            <Link to="/shop" className="btn-gold" data-testid="hero-shop-now-btn">
              Shop Now <ArrowRight strokeWidth={1.5} size={16} />
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {slides.map((_, i) => (
          <button
            key={i}
            data-testid={`hero-dot-${i}`}
            onClick={() => setActive(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-[2px] transition-all duration-500 ${
              active === i ? "w-10" : "w-5 bg-white/30"
            }`}
            style={active === i ? { background: "#F5D565" } : {}}
          />
        ))}
      </div>
    </section>
  );
}
